// ================================================================
// Settings Modal — تنظیمات یه node
// ================================================================

import { t } from '../../i18n/index.js';
import { icon, escapeHtml } from '../../utils/dom.js';
import { state } from '../../core/state.js';
import { events, EVENTS } from '../../core/events.js';
import { nodeRegistry } from '../../nodes/registry.js';
import { renderFields, cleanInvalidValues } from './FieldRenderer.js';
import { setDynamicOptionsOnUpdate, clearPromptVersionsCache } from '../../nodes/ai-analysis/dynamic-options.js';
import { startOAuthFlow } from '../../core/oauth.js';
import { apiRequest } from '../../core/apiClient.js';

let backdropEl = null;
let modalEl = null;
let currentNodeId = null;
let workingConfig = {};

export function initSettingsModal(root) {
  const backdrop = document.createElement('div');
  backdrop.className = 'modal-backdrop hidden';
  backdrop.id = 'settings-backdrop';
  backdrop.innerHTML = `<div class="modal modal-settings" id="settings-modal-inner"></div>`;
  root.appendChild(backdrop);

  backdropEl = backdrop;
  modalEl = backdrop.querySelector('#settings-modal-inner');

  backdropEl.addEventListener('mousedown', (e) => {
    if (e.target === backdropEl) close();
  });

  events.on(EVENTS.MODAL_OPEN_SETTINGS, ({ nodeId }) => open(nodeId));
}

function open(nodeId) {
  const node = state.getNode(nodeId);
  if (!node) return;
  const def = nodeRegistry.get(node.type);
  if (!def) return;

  currentNodeId = nodeId;
  workingConfig = { ...node.config };

  render(def);
  // ریست موقعیت drag — هر بار از وسط باز شه
  modalEl.classList.remove('was-dragged', 'is-dragging');
  modalEl.style.left = '';
  modalEl.style.top = '';
  backdropEl.classList.remove('hidden');
}

function close() {
  backdropEl.classList.add('hidden');
  currentNodeId = null;
  workingConfig = {};
  clearPromptVersionsCache();
}

function render(def) {
  modalEl.style.setProperty('--accent-color', def.color);
  modalEl.innerHTML = `
    <div class="settings-header">
      <h2 class="settings-title">${escapeHtml(t(def.modalTitleKey))}</h2>
      <button class="settings-close" data-action="close" title="${t('modal.close')}">
        ${icon('close', 20)}
      </button>
    </div>

    <div class="settings-accent"></div>

    <div class="settings-body" id="settings-body">
      ${renderFields(def.schema, workingConfig, def)}
    </div>

    <div class="settings-footer">
      <button class="btn-secondary" data-action="cancel">${t('modal.cancel')}</button>
      <button class="btn-primary" data-action="save">${t('modal.save')}</button>
    </div>
  `;

  setDynamicOptionsOnUpdate(() => rerenderBody(def));
  attachHandlers(def);
}

function rerenderBody(def) {
  const body = modalEl.querySelector('#settings-body');
  body.innerHTML = renderFields(def.schema, workingConfig, def);
  attachFieldHandlers(def);
}

function attachHandlers(def) {
  modalEl.querySelector('[data-action="close"]').addEventListener('click', close);
  modalEl.querySelector('[data-action="cancel"]').addEventListener('click', close);
  modalEl.querySelector('[data-action="save"]').addEventListener('click', () => {
    const cleaned = cleanInvalidValues(def.schema, workingConfig);
    state.updateNodeConfig(currentNodeId, cleaned);
    close();
  });

  attachFieldHandlers(def);
  attachDragHandler();
}

function attachFieldHandlers(def) {
  attachDropdownHandlers(def);
  attachTextInputHandlers(def);
  attachTextareaHandlers(def);
  attachCheckboxHandlers(def);
  attachOAuthHandlers(def);
  attachTestHandler(def);
  attachRepeaterHandlers(def);
}

// ─────────────────────────────────────────────────────────
// Dropdownها (شرطی)
// ─────────────────────────────────────────────────────────
function attachDropdownHandlers(def) {
  modalEl.querySelectorAll('.dropdown-trigger').forEach(trigger => {
    trigger.addEventListener('click', (e) => {
      e.stopPropagation();
      const dropdown = trigger.closest('.dropdown');
      modalEl.querySelectorAll('.dropdown.open').forEach(d => {
        if (d !== dropdown) { d.classList.remove('open'); resetDropdownMenu(d); }
      });
      const willOpen = !dropdown.classList.contains('open');
      dropdown.classList.toggle('open');
      if (willOpen) positionDropdownMenu(dropdown);
      else resetDropdownMenu(dropdown);
    });
  });

  // بستن دراپ‌داون‌ها هنگام اسکرول بدنه (چون منو fixed است و دنبال دکمه نمی‌آید)
  const body = modalEl.querySelector('.settings-body');
  if (body && !body.dataset.dropdownScrollAttached) {
    body.addEventListener('scroll', () => {
      modalEl.querySelectorAll('.dropdown.open').forEach(d => {
        d.classList.remove('open');
        resetDropdownMenu(d);
      });
    });
    body.dataset.dropdownScrollAttached = 'true';
  }

  modalEl.querySelectorAll('.dropdown-option').forEach(opt => {
    opt.addEventListener('click', (e) => {
      e.stopPropagation();
      const key = opt.dataset.field;
      const value = opt.dataset.value;

      workingConfig[key] = value;
      workingConfig = cleanInvalidValues(def.schema, workingConfig);

      // re-render body تا فیلدهای شرطی به‌روز شن
      rerenderBody(def);
    });
  });

  // کلیک بیرون
  if (!modalEl.dataset.outsideHandlerAttached) {
    modalEl.addEventListener('click', (e) => {
      if (!e.target.closest('.dropdown')) {
        modalEl.querySelectorAll('.dropdown.open').forEach(d => {
          d.classList.remove('open');
          resetDropdownMenu(d);
        });
      }
    });
    modalEl.dataset.outsideHandlerAttached = 'true';
  }
}

// منوی دراپ‌داون را با position:fixed نسبت به دکمه قرار می‌دهد تا از
// overflow بدنه‌ی مودال بیرون بزند و بریده نشود؛ کمبود فضا → باز شدن به بالا.
function positionDropdownMenu(dropdown) {
  const trigger = dropdown.querySelector('.dropdown-trigger');
  const menu = dropdown.querySelector('.dropdown-menu');
  if (!trigger || !menu) return;

  const r = trigger.getBoundingClientRect();
  const gap = 4;
  const margin = 12;
  const spaceBelow = window.innerHeight - r.bottom;
  const spaceAbove = r.top;

  menu.style.position = 'fixed';
  menu.style.left = r.left + 'px';
  menu.style.width = r.width + 'px';
  menu.style.right = 'auto';

  if (spaceBelow < 180 && spaceAbove > spaceBelow) {
    menu.style.top = 'auto';
    menu.style.bottom = (window.innerHeight - r.top + gap) + 'px';
    menu.style.maxHeight = Math.max(120, spaceAbove - margin) + 'px';
  } else {
    menu.style.bottom = 'auto';
    menu.style.top = (r.bottom + gap) + 'px';
    menu.style.maxHeight = Math.max(120, spaceBelow - margin) + 'px';
  }
}

function resetDropdownMenu(dropdown) {
  const menu = dropdown.querySelector('.dropdown-menu');
  if (menu) menu.style.cssText = '';
}

// ─────────────────────────────────────────────────────────
// Text/Email/Password/Number — بدون re-render، فقط update workingConfig
// ─────────────────────────────────────────────────────────
function attachTextInputHandlers(def) {
  modalEl.querySelectorAll('input.text-input').forEach(input => {
    input.addEventListener('input', () => {
      const key = input.dataset.field;
      const type = input.dataset.type;
      workingConfig[key] = type === 'number' ? Number(input.value) : input.value;
    });
  });
}

// ─────────────────────────────────────────────────────────
// Textarea — بدون re-render، فقط update workingConfig
// ─────────────────────────────────────────────────────────
function attachTextareaHandlers(def) {
  modalEl.querySelectorAll('textarea.textarea-input').forEach(ta => {
    ta.addEventListener('input', () => {
      workingConfig[ta.dataset.field] = ta.value;
    });
  });
}

function attachRepeaterHandlers(def) {
  // تایپ متن در هر خط — فقط workingConfig، بدون re-render
  modalEl.querySelectorAll('input.repeater-input').forEach(inp => {
    inp.addEventListener('input', () => {
      const key = inp.dataset.field;
      const index = Number(inp.dataset.index);
      if (!Array.isArray(workingConfig[key])) workingConfig[key] = [];
      workingConfig[key][index] = inp.value;
    });
  });
  // افزودن خط جدید — workingConfig + re-render
  modalEl.querySelectorAll('.repeater-add').forEach(btn => {
    btn.addEventListener('click', () => {
      const key = btn.dataset.repeaterAdd;
      if (!Array.isArray(workingConfig[key])) workingConfig[key] = [];
      workingConfig[key].push('');
      rerenderBody(def);
    });
  });
  // حذف خط — workingConfig + re-render
  modalEl.querySelectorAll('.repeater-remove').forEach(btn => {
    btn.addEventListener('click', () => {
      const key = btn.dataset.repeaterRemove;
      const index = Number(btn.dataset.index);
      if (Array.isArray(workingConfig[key])) {
        workingConfig[key].splice(index, 1);
        rerenderBody(def);
      }
    });
  });
}

// ─────────────────────────────────────────────────────────
// Checkboxها
// ─────────────────────────────────────────────────────────
function attachCheckboxHandlers(def) {
  modalEl.querySelectorAll('input.checkbox-input').forEach(input => {
    input.addEventListener('change', () => {
      workingConfig[input.dataset.field] = input.checked;
    });
  });
}

// ─────────────────────────────────────────────────────────
// OAuth — کلیک روی Connect یا Disconnect
// ─────────────────────────────────────────────────────────
function attachOAuthHandlers(def) {
  // Connect
  modalEl.querySelectorAll('.oauth-connect').forEach(btn => {
    btn.addEventListener('click', async (e) => {
      e.preventDefault();
      const fieldKey = btn.dataset.field;
      const providerId = btn.dataset.provider;
      const authUrl = btn.dataset.authUrl;

      try {
        // در آینده وقتی backend آماده شد، این تابع OAuth واقعی رو راه می‌اندازه
        // فعلاً یه placeholder result برمی‌گردونه تا UI تست بشه
        const result = await startOAuthFlow(providerId, authUrl);
        if (result) {
          workingConfig[fieldKey] = { ...result, _justConnected: true };
          rerenderBody(def);
          setTimeout(() => {
            if (workingConfig[fieldKey]) delete workingConfig[fieldKey]._justConnected;
          }, 1500);
        }
      } catch (err) {
        console.error('OAuth failed:', err);
        alert(t('nodes.email.errors.oauthFailed'));
      }
    });
  });

  // Verknüpfung dieses Knotens aufheben — bewusst NUR lokal.
  // Die Connection selbst gehört der Organisation und wird auch von der E-Mail-Seite
  // und vom Dokumentversand genutzt; sie hier zu löschen würde beides mitreissen.
  // Wirklich trennen (inkl. Widerruf bei Google): Einstellungen → Verbindungen.
  modalEl.querySelectorAll('.oauth-disconnect').forEach(btn => {
    btn.addEventListener('click', (e) => {
      e.preventDefault();
      const fieldKey = btn.dataset.field;
      delete workingConfig[fieldKey];
      rerenderBody(def);
    });
  });
}

// ─────────────────────────────────────────────────────────
// Test — خواندن آزمایشی ایمیل‌ها داخل modal
// ─────────────────────────────────────────────────────────
function attachTestHandler() {
  const btn = modalEl.querySelector('.oauth-test');
  if (!btn) return;

  btn.addEventListener('click', async (e) => {
    e.preventDefault();
    const connectionId = btn.dataset.connectionId;
    const resultEl = modalEl.querySelector('[data-test-result]');
    if (!connectionId) {
      resultEl.innerHTML = `<div class="test-error">${escapeHtml(t('nodes.email.errors.oauthFailed'))}</div>`;
      return;
    }

    btn.disabled = true;
    resultEl.innerHTML = `<div class="test-loading"><span class="run-spinner"></span> ${escapeHtml(t('nodes.email.testing'))}</div>`;

    try {
      const data = await apiRequest(`/api/connections/${connectionId}/gmail/messages`);

      if (!data.messages?.length) {
        resultEl.innerHTML = `<div class="test-empty">${escapeHtml(t('nodes.email.testNoEmails'))}</div>`;
        return;
      }

      const stripEmail = (from) => {
        if (!from) return '';
        const m = from.match(/^"?([^"<]+?)"?\s*</);
        return m ? m[1].trim() : from;
      };

      resultEl.innerHTML = `
        <div class="test-success-head">${icon('check_circle', 16)} ${data.count} ${escapeHtml(t('bottombar.emailsRead'))} · ${escapeHtml(data.account)}</div>
        <div class="test-emails">
          ${data.messages.map((m) => `
            <div class="test-email">
              <div class="test-email-from">${escapeHtml(stripEmail(m.from))}</div>
              <div class="test-email-subject">${escapeHtml(m.subject || '(kein Betreff)')}</div>
            </div>
          `).join('')}
        </div>
      `;
    } catch (err) {
      resultEl.innerHTML = `<div class="test-error">${escapeHtml(err.message || 'Fehler')}</div>`;
    } finally {
      btn.disabled = false;
    }
  });
}

// ─────────────────────────────────────────────────────────
// Drag — جابه‌جایی modal با کشیدن header
// ─────────────────────────────────────────────────────────
function attachDragHandler() {
  const header = modalEl.querySelector('.settings-header');
  if (!header) return;

  let startX = 0, startY = 0, originLeft = 0, originTop = 0, dragging = false;

  header.addEventListener('mousedown', (e) => {
    // اگه روی دکمه‌ی close کلیک شد، drag نکن
    if (e.target.closest('[data-action="close"]')) return;
    e.preventDefault();

    // مختصات فعلی modal رو بگیر (قبل از اینکه از مرکز flex جداش کنیم)
    const rect = modalEl.getBoundingClientRect();
    originLeft = rect.left;
    originTop = rect.top;
    startX = e.clientX;
    startY = e.clientY;
    dragging = true;

    // modal رو از حالت flex-center به position آزاد ببر
    modalEl.classList.add('is-dragging');
    modalEl.style.left = originLeft + 'px';
    modalEl.style.top = originTop + 'px';

    document.addEventListener('mousemove', onMove);
    document.addEventListener('mouseup', onUp);
  });

  function onMove(e) {
    if (!dragging) return;
    const dx = e.clientX - startX;
    const dy = e.clientY - startY;
    let newLeft = originLeft + dx;
    let newTop = originTop + dy;

    // نذار از صفحه بیرون بره (یه حاشیه‌ی کوچیک نگه‌دار)
    const w = modalEl.offsetWidth;
    const margin = 40;
    newLeft = Math.max(-w + margin, Math.min(window.innerWidth - margin, newLeft));
    newTop = Math.max(0, Math.min(window.innerHeight - margin, newTop));

    modalEl.style.left = newLeft + 'px';
    modalEl.style.top = newTop + 'px';
  }

  function onUp() {
    dragging = false;
    modalEl.classList.remove('is-dragging');
    modalEl.classList.add('was-dragged'); // موقعیت رو نگه‌دار
    document.removeEventListener('mousemove', onMove);
    document.removeEventListener('mouseup', onUp);
  }
}
